<template>
    <div class="container">
        <h1>{{$t('accommodation.block-dates')}}</h1>
        
        <!-- ical link form field -->
        <form class="d-flex justify-content-between mb-4">
            <input
                type="text"
                class="form-control flex-grow-1 mr-2"
                id="ical-link"
                v-model="formIcalUrl"
            />
            <!-- inline save button -->
            <button
                class="btn btn-primary mr-2"
                @click.prevent="handleIcalUrlSubmit"
                :disabled="formIcalUrl === icalUrl"
            >
                {{$t('block-dates.save')}}
            </button>

            <!-- inline run sync button -->
            <button
                class="btn btn-primary"
                @click.prevent="handleSync()"
                :disabled="formIcalUrl !== icalUrl"
            >
                {{$t('block-dates.run-sync')}}
            </button>
            
        </form>

        <!-- Los días ya bloqueados entran como deshabilitados: el calendario es
             la foto de lo que está cerrado, no solo un selector de rango. El ref
             es lo que después permite refrescarlo al crear o borrar un bloqueo. -->
        <DatePicker ref="calendar" :disabled-dates="disabledDates" />

        <div class="btn-save mb-5">
            <router-link to="/dashboard">
                <button class="btn mr-3"> {{$t('simulator.back')}} </button>
            </router-link>
            <button
                @click="saveRestriction"
                class="btn"
                type="submit"
                :disabled="dates.fecha_inicio === null || dates.fecha_termino === null || loading"
            >
                <b-spinner style="width:20px; height:20px" v-if="loading"/>
                {{$t('bank.save')}}
            </button>
        </div>
        
        <div v-for="date in block_dates" :key="date.idrestriccion" class="row date-block" >
            <div class="col-8">
                {{`${$t('block-dates.from')}: ${date.fecha_inicio} - ${$t('block-dates.to')}: ${date.fecha_termino}`}}
            </div>
            <div class="col-2">
                <span>{{ date.created_with }}</span>
            </div>
            <div class="col-2 text-left text-sm-right">
                <a
                    href="javascript:void(0)"
                    @click="eraseRestrict(date.idrestriccion)"
                    class="btn-delete"
                >
                    <i class="far fa-trash-alt" />
                </a>
            </div>
            <hr/>
        </div>

        <b-modal id="info-modal" size="lg" ref="info-modal" :title="$t('block-dates.title-modal')" hide-footer>
            <p class="my-4">{{$t('block-dates.error-info')}}</p>
            <hr>
            <div class="row">
                <div class="col-12 text-right">
                    <button class="mx-2 btn btn-outline-primary" @click="closeModal" id="block-dates-ok-button">Ok</button>                    
                </div>
            </div>
        </b-modal>
        
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import format from "date-fns/format";
import DatePicker from "@/new-components/DatePicker/DatePicker.vue";

export default {
    name: "BlockDates",
    components: {
        DatePicker
    },
    data() {
        return {
            // dates: {
            //     fecha_inicio: null,
            //     fecha_termino: null,                
            // },
            loading: false,
            formIcalUrl: ""
        }
    },
    methods: {
        
        ...mapActions("restriction", [
            "postIcalUrl",
            "fetchIcalUrl",
            "syncWithExternalIcal"
        ]),
        
        ...mapActions([
            "createRestriction",
            "getRestriction",
            "deleteRestriction"
        ]),

        async handleSync() {
            
            const response = await this.syncWithExternalIcal(this.accommodationId);

            const message = response ? this.$t('block-dates.sync-success') : this.$t('block-dates.sync-fail');

            this.refreshRestrictionsTable();

            // Show  message
            let toast = this.$toasted.show(message, {
                theme: "toasted-primary",
                position: "bottom-right",
                duration : 5000,
                className: "toasted"
            });
        },
        
        async handleIcalUrlSubmit() {

            const response = await this.postIcalUrl({
                icalUrl: this.formIcalUrl,
                accommodationId: this.accommodationId
            });

            const message = response ? this.$t('block-dates.url-saved') : this.$t('block-dates.url-not-saved');
            
            // Show success message
            let toast = this.$toasted.show(message, {
                theme: "toasted-primary",
                position: "bottom-right",
                duration : 5000,
                className: "toasted"
            });
            
        },
        
        saveRestriction() {
            this.loading = true;
            this.createRestriction(this.dates).then((response) => {
                this.loading = false;
                // El refresco devuelve la promesa: el calendario se repinta
                // recién cuando la lista nueva ya está en el store.
                return this.refreshRestrictionsTable();
            }).catch((error) => {
                this.loading = false;
                // Un error de red o un bug del front llegan sin `response`, y
                // leerle .status a undefined tiraba un segundo error que tapaba
                // al primero. Solo el 400 —fechas pisadas— muestra el modal.
                const status = (error && error.response) ? error.response.status : null;
                if (status === 400) {
                    this.$refs['info-modal'].show();
                    this.refrescarCalendario();
                } else if (status) {
                    this.$router.push('/dashboard');
                } else {
                    console.error('[BlockDates] no se pudo guardar el bloqueo:', error);
                }
            })

        },

        refreshRestrictionsTable() {
            return this.getRestriction().then((resp) => {
                this.$store.commit('setBlock_dates', resp.data);
                this.refrescarCalendario();
            });
        },

        eraseRestrict(id) {
            this.deleteRestriction(id).then((resp) => {
                this.$store.commit('setBlock_dates', resp.data.restrictions);
                this.refrescarCalendario();
            }).catch((error) => {
                // Si el borrado no pasó (permisos, la restricción ya no existe),
                // la lista de la pantalla no puede quedar mostrando algo distinto
                // de lo que hay en la base: se vuelve a pedir la verdad.
                console.error('[BlockDates] no se pudo borrar el bloqueo:', error);
                this.refreshRestrictionsTable();
            })
        },

        /**
         * Repinta el calendario con los bloqueos que hay AHORA en el store.
         *
         * Va en $nextTick porque el calendario relee `disabledDates` recién
         * cuando se lo pide clearSelection(), y para entonces la prop tiene que
         * tener ya el array nuevo. Sin esto, borrar un bloqueo lo sacaba de la
         * lista de abajo pero los días seguían tachados arriba.
         */
        refrescarCalendario() {
            this.$nextTick(() => {
                if (this.$refs.calendar && this.$refs.calendar.clearSelection) {
                    this.$refs.calendar.clearSelection();
                }
            });
        },
        closeModal() {
            this.$refs['info-modal'].hide()
        },

        /**
         * 'YYYY-MM-DD' (venga pelada o con hora atrás) → Date a medianoche LOCAL.
         *
         * A mano y no con new Date(texto): el string de solo fecha lo parsea el
         * navegador como UTC, y al oeste de Greenwich eso devuelve el día
         * anterior. Es el mismo corrimiento de zona horaria que ya se había
         * arreglado en el calendario de la ficha del alojamiento.
         */
        aFechaLocal(valor) {
            const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor || ''));
            if (!m) return null;
            return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        },

        /** Date → 'YYYY-MM-DDT00:00:00', sin la Z, para que se lea en hora local. */
        aIsoLocal(fecha) {
            const dosCifras = (n) => (n < 10 ? '0' + n : String(n));
            return fecha.getFullYear()
                + '-' + dosCifras(fecha.getMonth() + 1)
                + '-' + dosCifras(fecha.getDate())
                + 'T00:00:00';
        }
    },
    computed: {
        
        ...mapState('search', [
            "checkInDate",
            "checkOutDate",
        ]),
        
        ...mapState("restriction", [
            "icalUrl"
        ]),
        
        ...mapState(["loader", "blockdates", "block_dates"]),
        
        accommodationId() {
            // Get id from url
            return this.$route.params.id;
        },
        
        dates() {
            return {
                fecha_inicio: this.checkInDate,
                fecha_termino: this.checkOutDate
            }
        },

        /**
         * Los días que el calendario tiene que mostrar cerrados: cada bloqueo de
         * la lista, día por día y con los dos extremos incluidos.
         *
         * Sale de `block_dates`, que es la misma lista que se ve abajo. Al ser un
         * computed sobre el store, borrar un bloqueo lo saca de acá solo; lo que
         * no es automático es que el calendario lo relea, y de eso se ocupa
         * refrescarCalendario().
         */
        disabledDates() {
            const dias = [];

            (this.block_dates || []).forEach((bloque) => {
                const desde = this.aFechaLocal(bloque.fecha_inicio);
                const hasta = this.aFechaLocal(bloque.fecha_termino);
                if (!desde || !hasta || hasta < desde) return;

                // Tope de seguridad: un bloqueo de años —los que a veces deja la
                // sincronización con otros portales— generaría miles de fechas y
                // dejaría la pantalla pensando al abrir el calendario.
                let vueltas = 0;
                const cursor = new Date(desde);
                while (cursor <= hasta && vueltas < 750) {
                    dias.push(this.aIsoLocal(cursor));
                    cursor.setDate(cursor.getDate() + 1);
                    vueltas++;
                }
            });

            return dias;
        },
    },
    created() {
        // Get the icalUrl from the server, save it to the store, and set it in the form
        this.fetchIcalUrl(this.accommodationId)
            .then(() => {
                this.formIcalUrl = this.icalUrl;
            });
        
        this.getRestriction()
        .then((response) => {
            this.$store.commit('setBlock_dates', response.data);
            // La lista llega después de que el calendario ya se montó, y el
            // calendario lee las fechas cerradas una sola vez al montarse: sin
            // este repintado, la primera carga no muestra ningún día bloqueado.
            this.refrescarCalendario();
        }).catch((error) => {
            this.$router.push('/dashboard');
        });
    }
};
</script>

<style lang="scss" scoped>

    .btn-outline-primary {
        background-color: #FFF;
        border-color: $hf-sec-pine !important;
        color: $hf-sec-pine;
        &:hover{
            color: #fff;
            background-color: $hf-sec-pine;
            border-color: $hf-sec-pine;
        }
    }
.container {
    
    .btn {
        background-color: $hf-primary;
        border: none;
    }
    
    h1 {
        color: $hf-sec-pine;
        font-size: 1.8em;
        //border-bottom: solid rgb(231, 231, 231) 0.5px;
        padding-bottom: 10px;
        margin-bottom: 20px;
        text-align: right;
    }

    .btn-save {
        margin-top: 10px;
        text-align: right;
        button {
            color: white;
            background-color: $hf-sec-pine;
        }
    }

    .btn-delete {
        color: red;
        font-size: 1.1em;
    }

    .date-block{
        font-size: .8em;
    }

    hr {
        width: 90%;
    }
}

@media (min-width: 767px) {
    hr{
        width: 100% !important;
    }
}
</style>

<style>

.datepicker {
    z-index: 1021 !important;
}

// Next month button
.next--mobile {
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    bottom: 0;
    background: $hf-sec-pine;
    height: $see-more-button-height;
    color: #FFFFFF;
    font-size: 1.2rem;
    font-style: italic;
    z-index: 1021 !important;
}

</style>


